/** @module csi/widget/slicerMetricFormat */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"dijit/_WidgetBase",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetsInTemplateMixin",
	"dgrid/OnDemandGrid",
	"dgrid/Selection",
	"csi/_base/underscore",
	"csi/api",
	"csi/html",
	"csi/locale",
	"csi/types",
	"dojo/text!./slicerMetricFormat.html",
	// Used in template:
	"csi/widget/Select",
	"dijit/form/TextBox",
	"dijit/form/ToggleButton"
], function(declare,Memory,_WidgetBase,_TemplatedMixin,_WidgetsInTemplateMixin,OnDemandGrid,Selection,_,api,html,locale,types,template) {

	/**
	 * Array of data for Format Drop Down
	 * @return {FormatDoubleDetails}
	 * @typedef {Object[]} FormatDoubleDetails
	 *  @property {string} id
	 *  @property {string} name
	 *  @property {boolean} bSeparator - allow choosing thousands separator
	 *  @property {boolean} bDecimals - allow choosing number of decimals after point
	 *  @property {number} numDecimals - default number of decimals
	 *  @property {string} prefix - string to be added on the left of the format (sample: "$", "$#,##"...) 
	 *  @property {string} suffix - string to be added on the right of the format  (sample: "%" ...)
	 *  @property {string} format - part of the format before adding prefix or suffix or decimals
	 *  	finally format will be calculated as: {prefix} + {ThousandsSeparator} + {format} + {"#" x numDecimals} + {suffix}
	 *  @property {boolean} bNoFormat - no specific format, show number as is  
	 *  @property {boolean} bSpecial - formats defined by user via "site.js"  
	 *  @property {boolean} bCustom - custom user format, show text editor  
	 *  @property {number} [nFactor] - sample value multiplier: 0.00001 for percentage, use "1" if undefined  
	 */
	function getFormatDoubleDetails(){
		return [
			{
				id: "1",
				name: locale.resource("FormatDoubleNoFormat"),
				bSeparator: false,
				bNoFormat: true,
				prefix: "",
				suffix: "",
				format: ""
			},
			{
				id: "2",
				name: locale.resource("FormatDoubleNumber"),
				bSeparator: true,
				bDecimals: true,
				numDecimals: 2,
				prefix: "",
				suffix: "",
				format: "0."
			},
			{
				id: "3",
				name: locale.resource("FormatDoubleCurrency"),
				bSeparator: false,
				bDecimals: true,
				numDecimals: 2,
				prefix: "$#,##",
				suffix: "",
				format: "0."
			},
			{
				id: "4",
				name: locale.resource("FormatDoublePercentage"),
				bSeparator: false,
				bDecimals: true,
				numDecimals: 2,
				nFactor: 0.00001,
				prefix: "",
				suffix: "%",
				format: "0."
			},
			{
				id: "5",
				name: locale.resource("FormatDoubleSpecial"),
				bSpecial: true
			},
			{
				id: "6",
				name: locale.resource("FormatDoubleCustom"),
				bCustom: true
			}
		];
	}
	
	var THOUSAND_SEPARATOR = "#,##";
	var SAMPLE_VALUE = 12345.67;
	var FIELD_VALUE = "value";
	var FIELD_CHECKED = "checked";
	var US_DECIMAL = ".";
	var US_SEPARATOR = ",";
	var SPACE_160 = String.fromCharCode(160); //non-breaking space, character code 160;
	var SPACE_32 = String.fromCharCode(32); // normal space, character code 32;
	
	/**
	 * Return string of "ch" repeated "num" times
	 * @param {string} ch
	 * @param {number} num
	 * @return {string} 
	 */
	function repeatChar(ch, num){
		var res = "";
		for (var i=0; i<num;i++){
			res += ch;
		}
		return res;
	}
	
	return declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin],{
		templateString: template,
		parseOnLoad: true,
		
		/** 
		 * @param {FormatPresentation} formatPresentation
		 * @typedef {Object} FormatPresentation
		 * 	@param {string} id - id of selected format type  
		 * 	@param {string} decimals - value of number of decimals select 
		 * 	@param {boolean} separator - property "checked" of "Thousand Separators" switch  
		 * 	@param {string} special - selection of "Special" select
		 * 	@param {string} custom - value of custom format text box
		 * 	@param {string} value - widget value
		 * 	@param {string} valueLabel - drop down button label
		 */ 
		formatPresentation: null,
		/** @param {dgrid} grid - format mode selection grid */
		grid: null,
		/** @param {Object} parentButton - reference to parent drop down button control */
		parentButton: null,
		/** @param {FormatDoubleDetails} selected */
		selected: null,
		
		/** @param {string} value - selected format value */
		value: "", 
		
		startup: function(){
			var self = this;
			var fmtDouble = getFormatDoubleDetails();
			
			if (this.isSpecialEmpty()){
				fmtDouble.splice(4,1); // remove special selection
			}
	  		var typesStore = new Memory({data: fmtDouble});
			var grid = this.grid = new declare(([OnDemandGrid, Selection]))({
				columns:[{field:"name"}],
				selectionMode: "single",
				showHeader: false,
				store: typesStore
			}, this.selectNode);
			grid.startup();
			grid.on("dgrid-select", function(event){
				self.onGridSelect(event);
			});
			
			this.inherited(arguments);
			
			this.specialNodeControl.set("store", this.getSpecialStore());
			this.setPresentation(this.formatPresentation);
			this.setSample();
		},
		
		/**
		 * Convert given format from user locale to US locale. Replace decimal and thousands separators.
		 * @param {string} value - format in user locale
		 * @return {string} - format in US locale
		 */
		userToUSLocale: function(value){
			var result = "";
			var decimal = types.getNumberDecimal();
			var separator = types.getNumberGroup();
			for (var i=0; i<value.length;i++){
				var val = value[i];
				if (val == SPACE_32){
					val = SPACE_160;
				}
				if (val == decimal){
					result += US_DECIMAL;
				} else if (val == separator){
					result += US_SEPARATOR;
				} else {
					result += val;
				}
 			}
			return result;
		},
		
		/**
		 * Convert given format from US locale to user locale. Replace decimal and thousands separators.
		 * @param {string} value - format in US locale
		 * @return {string} - format in user locale
		 */
		USToUserLocale: function(value){
			var result = "";
			var decimal = types.getNumberDecimal();
			var separator = types.getNumberGroup();
			for (var i=0; i<value.length;i++){
				if (value[i] == US_DECIMAL){
					result += decimal;
				} else if (value[i] == US_SEPARATOR){
					result += separator;
				} else {
					result += value[i];
				}
			}
			return result;
		},

		/** 
		 * Create storage for formats defined via site.js
		 * @return {Memory}
		 */
		getSpecialStore: function(){
			var data = api.getAddedNumberFormats();
			data = _.map(data, function(fmt){
				return {id: _.uniqueId(), value:fmt};
			});
			return new Memory({data:data, getLabel: function(item){
				var val = item.value;
				return locale.resourceIfExists("Format_" + val) || val;
			}});
		},
		
		/**
		 * @return {string} - return format string selected in special formats select 
		 */
		getSpecialValue: function(){
			var ctl = this.specialNodeControl;
			var obj = ctl.store.get(ctl.get(FIELD_VALUE));
			return (obj && obj.value) || "";
		},
		
		/**
		 * @return {string} - return name of format, selected in special formats select
		 */
		getSpecialName: function(){
			var ctl = this.specialNodeControl;
			var store = ctl.store;
			var obj = store.get(ctl.get(FIELD_VALUE));
			return (obj && store.getLabel(obj)) || "";
		},
		
		/**
		 * @param {string} format - actual format string
		 * @return {string} - return id of the line, selected in special formats select
		 */
		getSpecialId: function(format){
			var store = this.specialNodeControl.store;
			var res = store.query({value:format});
			if (res.length){
				return res[0].id;
			}
			var options = {count:1};
			res = store.query({}, options);
			return res.length && res[0];
		},
		
		/** 
		 * Return true if there are no number formats defined via site.js 
		 * @return {boolean}
		 */ 
		isSpecialEmpty: function(){
			return api.getAddedNumberFormats().length == 0;
		},
		
		/**
		 * @return {string} - return actual format value
		 */
		getValue: function(){
			var sel = this.selected;
			if (sel.bCustom){
				return this.userToUSLocale(this.customFormatNodeControl.get(FIELD_VALUE));
			}
			if (sel.bSpecial){
				return this.getSpecialValue();
			}
			var separator = sel.bSeparator && this.separatorNodeControl.checked ? THOUSAND_SEPARATOR : ""; // Thousands separator
			var decimals = sel.bDecimals ? repeatChar("0", this.numDecimalNodeControl.get(FIELD_VALUE)) : "";
			var format = sel.prefix + separator + sel.format + decimals + sel.suffix;
			return format;
		},
		
		/**
		 * Event happen when format value change via user actions
		 */
		onFormatChange: function(){
			this.value = this.getValue();
			this.setSample();
		},
		
		/**
		 * Event happen when user change selection of format kind
		 * @param {Event} event
		 */
		onGridSelect: function (event) {
			this.value = "";
			this.selected = event.rows[0].data;
			this.setSample();
			this.setVisibleControls();
			this.onFormatChange();
		},
		
		/**
		 * Create sample string and assign it to sample control and to this.valueLabel
		 */
		setSample: function(){
			var sel = this.selected;
			var value = this.value;
			var nFactor = this.selected.nFactor || 1;
			
			var sample = types.getNumberRead(SAMPLE_VALUE * nFactor, value);
			if (sel.bNoFormat){
				this.valueLabel = sel.name;
			} else if (sel.bSpecial){
				var name = this.getSpecialName();
				this.valueLabel = sel.name + (name ? ': ' + name : "");
			} else if (sel.bCustom){
				this.valueLabel = sel.name + (value ? ': "' + this.USToUserLocale(value) + '"' : "");
			} else {
				this.valueLabel = sel.name + (sample ? ": "+sample : "");
			}
			if (!sel.bCustom) {
				this.customFormatNodeControl.set(FIELD_VALUE, this.USToUserLocale(value)); // Assign current format to custom editor
			}
			html.setInnerText(this.sampleNode, sample);
			this.updateParent();
		},
		
		/**
		 * Create presentation of user selections to store it in metric's presentation.
		 * It will be used next time when user edit this metric to set up format selections
		 * @return {FormatPresentation}
		 */
		getPresentation: function(){
			var obj = {};
			obj.id = this.selected.id;
			obj.decimals = this.numDecimalNodeControl.get(FIELD_VALUE);
			obj.separator = this.separatorNodeControl.get(FIELD_CHECKED);
			obj.special = this.getSpecialValue();
			obj.custom = this.userToUSLocale(this.customFormatNodeControl.get(FIELD_VALUE));
			obj.value = this.value;
			obj.valueLabel = this.valueLabel;
			return obj;
		},
		
		/**
		 * Apply format presentation created with getPresentation() 
		 * @param {FormatPresentation} obj
		 */
		setPresentation: function(obj){
			if (obj && obj.id){
				this.grid.select(obj.id);
				this.numDecimalNodeControl.set(FIELD_VALUE, obj.decimals);
				this.separatorNodeControl.set(FIELD_CHECKED, obj.separator);
				this.specialNodeControl.set(FIELD_VALUE, this.getSpecialId(obj.special));
				this.customFormatNodeControl.set(FIELD_VALUE, this.USToUserLocale(obj.custom));
			} else if (this.parentButton.value){ // case when format was set via XML
				this.customFormatNodeControl.set(FIELD_VALUE, this.USToUserLocale(this.parentButton.value));
				this.grid.select("6"); // set custom format
			} else {
				this.grid.select("1"); // set no format
			}
		},
		
		/**
		 * Show/hide controls required for selected format kind
		 */
		setVisibleControls: function(){
			var sel = this.selected;
			html.setVisible(this.noFormatNode, sel.bNoFormat);
			html.setVisible(this.separatorNode, sel.bSeparator);
			html.setVisible(this.numDecimalNode, sel.bDecimals);
			html.setVisible(this.customFormatNode, sel.bCustom);
			html.setVisible(this.specialNode, sel.bSpecial);
		},
		
		/**
		 * Set parent drop down value and label
		 */
		updateParent: function(){
			this.parentButton.value = this.value;
			this.parentButton.set("label", this.valueLabel);
		}
		
	});
});